import { IconFont, Tooltip } from '@byecode/ui'
import React from 'react'

import type { I18TranslationKey } from '../../../types'
import type { TableAxisTarget, TableInsertDirection, TableSelectionTarget } from './core'
import type { InsertIndicatorState,TableAxisRenderConfig } from './TableControls.config'
import type { OverlayState, ResolvedSelectionState } from './TableControls.helpers'
import {
    CONTROLS_GAP,
    INDICATOR_THICKNESS,
    INSERT_HOTSPOT_SIZE,
    InsertHotspot,
    TRIGGER_THICKNESS,
    TriggerButton
} from './TableControls.styles'

const ColumnInsertHotspot: React.FC<{
    rect: DOMRect
    tableRect: DOMRect
    index: number
    title: string
    onHover: (payload: NonNullable<InsertIndicatorState>) => void
    onLeave: () => void
    onInsert: (direction: TableInsertDirection) => void
}> = ({ rect, tableRect, index, title, onHover, onLeave, onInsert }) => {
    const beforeLeft = rect.left - INSERT_HOTSPOT_SIZE / 2
    const afterLeft = rect.right - INSERT_HOTSPOT_SIZE / 2

    return (
        <>
            {index === 0 && (
                <Tooltip title={title}>
                    <InsertHotspot
                        data-rich-text-table-control=""
                        style={{
                            top: rect.top - CONTROLS_GAP - TRIGGER_THICKNESS - INSERT_HOTSPOT_SIZE / 2 + TRIGGER_THICKNESS / 2,
                            left: beforeLeft
                        }}
                        onMouseDown={event => {
                            event.preventDefault()
                            event.stopPropagation()
                        }}
                        onMouseEnter={() =>
                            onHover({
                                axis: 'column',
                                direction: 'before',
                                index,
                                top: tableRect.top,
                                left: rect.left,
                                width: INDICATOR_THICKNESS,
                                height: tableRect.height
                            })
                        }
                        onMouseLeave={onLeave}
                        onClick={() => onInsert('before')}
                    >
                        <IconFont type="Plus" size={12} />
                    </InsertHotspot>
                </Tooltip>
            )}
            <Tooltip title={title}>
                <InsertHotspot
                    data-rich-text-table-control=""
                    style={{
                        top: rect.top - CONTROLS_GAP - TRIGGER_THICKNESS - INSERT_HOTSPOT_SIZE / 2 + TRIGGER_THICKNESS / 2,
                        left: afterLeft
                    }}
                    onMouseDown={event => {
                        event.preventDefault()
                        event.stopPropagation()
                    }}
                    onMouseEnter={() =>
                        onHover({
                            axis: 'column',
                            direction: 'after',
                            index,
                            top: tableRect.top,
                            left: rect.right,
                            width: INDICATOR_THICKNESS,
                            height: tableRect.height
                        })
                    }
                    onMouseLeave={onLeave}
                    onClick={() => onInsert('after')}
                >
                    <IconFont type="Plus" size={12} />
                </InsertHotspot>
            </Tooltip>
        </>
    )
}

const RowInsertHotspot: React.FC<{
    rect: DOMRect
    tableRect: DOMRect
    index: number
    title: string
    onHover: (payload: NonNullable<InsertIndicatorState>) => void
    onLeave: () => void
    onInsert: (direction: TableInsertDirection) => void
}> = ({ rect, tableRect, index, title, onHover, onLeave, onInsert }) => {
    const beforeTop = rect.top - INSERT_HOTSPOT_SIZE / 2
    const afterTop = rect.bottom - INSERT_HOTSPOT_SIZE / 2

    return (
        <>
            {index === 0 && (
                <Tooltip title={title}>
                    <InsertHotspot
                        data-rich-text-table-control=""
                        style={{
                            top: beforeTop,
                            left: rect.left - CONTROLS_GAP - TRIGGER_THICKNESS - INSERT_HOTSPOT_SIZE / 2 + TRIGGER_THICKNESS / 2
                        }}
                        onMouseDown={event => {
                            event.preventDefault()
                            event.stopPropagation()
                        }}
                        onMouseEnter={() =>
                            onHover({
                                axis: 'row',
                                direction: 'before',
                                index,
                                top: rect.top,
                                left: tableRect.left,
                                width: tableRect.width,
                                height: INDICATOR_THICKNESS
                            })
                        }
                        onMouseLeave={onLeave}
                        onClick={() => onInsert('before')}
                    >
                        <IconFont type="Plus" size={12} />
                    </InsertHotspot>
                </Tooltip>
            )}
            <Tooltip title={title}>
                <InsertHotspot
                    data-rich-text-table-control=""
                    style={{
                        top: afterTop,
                        left: rect.left - CONTROLS_GAP - TRIGGER_THICKNESS - INSERT_HOTSPOT_SIZE / 2 + TRIGGER_THICKNESS / 2
                    }}
                    onMouseDown={event => {
                        event.preventDefault()
                        event.stopPropagation()
                    }}
                    onMouseEnter={() =>
                        onHover({
                            axis: 'row',
                            direction: 'after',
                            index,
                            top: rect.bottom,
                            left: tableRect.left,
                            width: tableRect.width,
                            height: INDICATOR_THICKNESS
                        })
                    }
                    onMouseLeave={onLeave}
                    onClick={() => onInsert('after')}
                >
                    <IconFont type="Plus" size={12} />
                </InsertHotspot>
            </Tooltip>
        </>
    )
}

interface TableAxisControlsProps {
    axisConfig: TableAxisRenderConfig
    anchorRects: NonNullable<OverlayState>['anchorRects']
    selectionState: ResolvedSelectionState | null
    convertTextByLanguage: (key: I18TranslationKey) => string
    setInsertIndicator: React.Dispatch<React.SetStateAction<InsertIndicatorState>>
    interactWithTarget: (target: TableSelectionTarget, options?: { contextMenuAt?: { x: number; y: number } }) => void
    executeInsertAt: (target: TableAxisTarget, direction: TableInsertDirection) => boolean
    interactingRef: React.MutableRefObject<boolean>
}

export const TableAxisControls: React.FC<TableAxisControlsProps> = ({
    axisConfig,
    anchorRects,
    selectionState,
    convertTextByLanguage,
    setInsertIndicator,
    interactWithTarget,
    executeInsertAt,
    interactingRef
}) => {
    if (!selectionState?.capabilities.showTriggers) {
        return null
    }

    const rects = axisConfig.axis === 'column' ? anchorRects.columnRects : anchorRects.rowRects
    const selectedIndexes = axisConfig.axis === 'column' ? selectionState.selectionInfo.colIndexes : selectionState.selectionInfo.rowIndexes

    return (
        <>
            {rects.map((rect, index) => {
                const target: TableAxisTarget = { kind: 'axis', axis: axisConfig.axis, index }
                const active = selectionState.activeAxis === axisConfig.axis && selectedIndexes.includes(index)
                const hotspotTitle = convertTextByLanguage(axisConfig.hotspotTitle)

                return (
                    <React.Fragment key={`${axisConfig.axis}-${index}`}>
                        <TriggerButton
                            data-rich-text-table-control=""
                            axis={axisConfig.axis}
                            active={active}
                            style={axisConfig.triggerStyle(rect)}
                            onMouseDown={event => {
                                event.preventDefault()
                                event.stopPropagation()
                                interactingRef.current = true
                            }}
                            onClick={() => {
                                interactWithTarget(target)
                            }}
                            onContextMenu={event => {
                                event.preventDefault()
                                event.stopPropagation()
                                interactWithTarget(target, {
                                    contextMenuAt: {
                                        x: event.clientX,
                                        y: event.clientY
                                    }
                                })
                            }}
                        />
                        {axisConfig.axis === 'column' ? (
                            <ColumnInsertHotspot
                                rect={rect}
                                tableRect={anchorRects.tableRect}
                                index={index}
                                title={hotspotTitle}
                                onHover={payload => {
                                    if (!selectionState.capabilities.showInsertHotspots) {
                                        return
                                    }
                                    setInsertIndicator(payload)
                                }}
                                onLeave={() => setInsertIndicator(current => (current?.axis === axisConfig.axis ? null : current))}
                                onInsert={direction => {
                                    executeInsertAt(target, direction)
                                }}
                            />
                        ) : (
                            <RowInsertHotspot
                                rect={rect}
                                tableRect={anchorRects.tableRect}
                                index={index}
                                title={hotspotTitle}
                                onHover={payload => {
                                    if (!selectionState.capabilities.showInsertHotspots) {
                                        return
                                    }
                                    setInsertIndicator(payload)
                                }}
                                onLeave={() => setInsertIndicator(current => (current?.axis === axisConfig.axis ? null : current))}
                                onInsert={direction => {
                                    executeInsertAt(target, direction)
                                }}
                            />
                        )}
                    </React.Fragment>
                )
            })}
        </>
    )
}
